<template>
  <template v-if="detectedPlatform && Object.keys(detectedPlatform).length > 0">
    <hr class="platform-runtime-settings__divider">
    <div class="platform-runtime-settings">
      <div class="platform-runtime-settings__heading">Runtime Settings</div>
      <div class="platform-runtime-settings__scroll">
        <div class="platform-runtime-settings__spec-grid">
          <template v-for="(row, index) in runtimeSettingsRows" :key="`${row.key}-${index}`">
            <div
              v-if="row.isGroupHeader"
              class="platform-runtime-settings__group-header"
            >
              {{ row.key }}
            </div>
            <div
              v-else
              class="platform-runtime-settings__spec-row"
              :class="{ 'platform-runtime-settings__spec-row--nested': row.level === 1 }"
            >
              <div class="platform-runtime-settings__spec-key">{{ row.key }}</div>
              <div class="platform-runtime-settings__spec-value">{{ row.value }}</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  detectedPlatform: {
    type: Object,
    default: () => null,
  },
});

const runtimeSettingsRows = computed(() => {
  if (!props.detectedPlatform || typeof props.detectedPlatform !== 'object') {
    return [];
  }

  const rows = [];

  Object.entries(props.detectedPlatform).forEach(([topKey, topValue]) => {
    if (topValue === null || topValue === undefined) {
      return;
    }

    // Check if value is a non-empty object
    if (typeof topValue === 'object' && !Array.isArray(topValue) && Object.keys(topValue).length > 0) {
      // Add the key as a group header
      rows.push({
        key: formatSpecKey(topKey),
        value: null,
        isGroupHeader: true,
        level: 0,
      });

      // Add nested items
      Object.entries(topValue).forEach(([nestedKey, nestedValue]) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const displayValue = typeof nestedValue === 'object' ? JSON.stringify(nestedValue) : formatValue(String(nestedValue));
          rows.push({
            key: formatSpecKey(nestedKey),
            value: displayValue,
            isGroupHeader: false,
            level: 1,
          });
        }
      });
    } else {
      // Simple value or empty object - display as regular row
      const displayValue = typeof topValue === 'object' ? JSON.stringify(topValue) : formatValue(String(topValue));
      rows.push({
        key: formatSpecKey(topKey),
        value: displayValue,
        isGroupHeader: false,
        level: 0,
      });
    }
  });

  return rows;
});

function formatValue(value) {
  // Detect ISO 8601 date strings and format them
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  if (isoDatePattern.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }
  return value;
}

function formatSpecKey(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
</script>

<style lang="scss" scoped>
.platform-runtime-settings__divider {
  margin: 0;
  border: none;
  border-top: 1px solid;
  width: 100%;
  @include themify() {
    border-color: rgba(0, 0, 0, 0.12);
  }
}

.platform-runtime-settings {
  padding: 0 2px;
  width: 100%;
}

.platform-runtime-settings__scroll {
  max-height: 400px;
  overflow-y: auto;
}

.platform-runtime-settings__heading {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  margin-top: 0;
  @include themify() {
    color: #888888;
  }
}

.platform-runtime-settings__group-header {
  font-weight: 600;
  font-size: 0.78rem;
  margin-top: 4px;
  margin-bottom: 2px;
  margin-left: 0;
  grid-column: 1 / -1;
  @include themify() {
    color: #555555;
  }
}

.platform-runtime-settings__spec-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.platform-runtime-settings__spec-row {
  display: grid;
  grid-template-columns: minmax(90px, 120px) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.platform-runtime-settings__spec-key {
  font-weight: 600;
  font-size: 0.78rem;
}

.platform-runtime-settings__spec-value {
  word-break: break-word;
  font-size: 0.78rem;
}

.platform-runtime-settings__spec-row--nested .platform-runtime-settings__spec-key {
  padding-left: 16px;
  font-weight: 600;
}

.platform-runtime-settings__spec-row--nested {
  margin-left: 0;
}
</style>
